// kfe-self-record-grid.js — the Self-Recording WORD GRID (entry surface).
//
// iOS CANON (Rule #9 — mirror exactly):
//   KFE-4.0-App/iEnglish/Classes/KFEModules/Learning/SelfRecording/
//     KFESRListPageVC.{h,m}      — the collection of word tiles
//     KFESRListCell.{h,m,xib}    — a single tile
//   reached from KFESelfRecordingVC.m's menuListBtn (→ the list page).
//
// Layout (KFESRListPageVC.m): a 3-column grid (kPerRowCount = 3; the
// index % kPerRowCount == {0,1,2} constraint cascade at m:117-156), square
// tiles (make.width == make.height == cellWidth, m:160-163).
//
// Tile (KFESRListCell.m + KFESRListPageVC.m:60-113):
//   • titleLabel    — the English word (textEn); AudioOnly w/ no text → "(Audio)" (m:89-92)
//   • bigImageView  — the image, shown for AudioImage / AudioImageText (m:60-92)
//   • smallCircleView (cornerRadius 10 → 20pt circle, top-right) holding either:
//       – littleGreenTickImageView  when isCompleted          (m:101-104)
//       – playIndexLabel "N/M" (playsSoFarCount/mustPlayCount) otherwise (m:106-110)
//   • setDisplayCellStyle (KFESRListCell.m:54-58): borderWidth 2, orange
//     0xff921d — applied to the CURRENT cell (index == displayingItemModel.tag, m:112-114)
//
// ASCII sketch:
//   ┌───────┐ ┌───────┐ ┌───────┐
//   │   ⊙0/1│ │  ⊙✓   │ │   ⊙0/1│      ⊙ = counter circle, top-right
//   │ brand │ │ cash  │ │  hat  │      cash = current (orange border)
//   └───────┘ └━━━━━━━┘ └───────┘      brand = ✓ completed
//
// Web tile content mirrors iOS: image for image display-types, the word for
// the rest. The orange border uses --kfe-accent (KFE orange, the token form
// of 0xff921d); the tick uses --kfe-success (KFE lime).
//
// Emits: "tile:tap" { mmuId, index } when a tile is tapped (→ host opens
// the practice screen at that word).

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { resolveImage } from "../data/media-paths.js";

/** @typedef {import("../engines/sr-engine.js").Concept} Concept */

export class KFESelfRecordGrid extends KFEComponent {
  // `progress` and `currentMmuId` re-render the grid in place (tile state
  // updates as the learner completes words / navigates).
  static props = ["concepts", "progress", "currentMmuId"];
  static styles = `
    :host { display: block; }
    .grid { display: grid; gap: 0.6rem;
            grid-template-columns: repeat(3, 1fr); }
    /* Tablet/desktop may widen past 3 columns (brief: "stays 3 at phone
       widths; tablet/desktop may widen"). */
    @media (min-width: 720px)  { .grid { grid-template-columns: repeat(4, 1fr); } }
    @media (min-width: 1024px) { .grid { grid-template-columns: repeat(6, 1fr); } }

    .tile { position: relative; aspect-ratio: 1 / 1;
            background: var(--kfe-surface); color: var(--kfe-fg);
            border: 2px solid var(--kfe-border); border-radius: 8px;
            display: flex; align-items: center; justify-content: center;
            overflow: hidden; cursor: pointer; padding: 0.4rem; box-sizing: border-box;
            text-align: center; font-size: 0.95rem; font-weight: 600; }
    /* Current tile — orange border (KFESRListCell setDisplayCellStyle). */
    .tile.current { border-color: var(--kfe-accent); }

    /* Image tiles get the canonical white image cell (transparent .png line
       drawings — same rule as kfe-tile-grid / SR stimulus, Brief 061). */
    .tile.image { background: var(--kfe-l2b-image-bg); }
    .tile.image img { width: 100%; height: 100%; object-fit: contain; }
    /* No asset resolved → paint the phrase on the white cell rather than a
       broken <img> (mirrors SR stimulus no-image, Brief 062). */
    .tile.image.no-image { color: var(--kfe-bg); }

    /* Counter circle, top-right (smallCircleView). */
    .badge { position: absolute; top: 4px; right: 4px;
             min-width: 20px; height: 20px; padding: 0 4px; box-sizing: border-box;
             border-radius: 999px; background: var(--kfe-bg);
             border: 1px solid var(--kfe-border);
             display: flex; align-items: center; justify-content: center;
             font-size: 0.7rem; font-weight: 600; color: var(--kfe-fg); }
    .badge.complete { background: var(--kfe-success); border-color: var(--kfe-success);
                      color: #1a1a1a; }
  `;

  async onConnected() {
    await this._resolveImages();
  }

  /**
   * Resolve images for the image display-type tiles. SR stimulus images are
   * L2B-style .png line drawings even though SR MMUs carry isL2b:false — pass
   * the imageExt:"png" hint so the resolver picks .png first (same as the SR
   * stimulus path, Brief 062).
   */
  async _resolveImages() {
    /** @type {Record<number,string>} */ this._urls = {};
    const concepts = this._concepts();
    for (const c of concepts) {
      if (!needsImage(c)) continue;
      try {
        const url = await resolveImage({ ...c, imageExt: "png" });
        if (url) this._urls[c.mmuId] = url;
      } catch { /* leave unresolved → no-image placeholder */ }
    }
    this._scheduleRender();
  }

  /** @returns {Concept[]} */
  _concepts() {
    return /** @type {any} */ (this).concepts ?? [];
  }

  /** @returns {Record<number, { playSoFar?: number, mustPlay?: number, completed?: boolean }>} */
  _progress() {
    return /** @type {any} */ (this).progress ?? {};
  }

  render() {
    const concepts = this._concepts();
    const progress = this._progress();
    const current = /** @type {any} */ (this).currentMmuId;

    const grid = document.createElement("div");
    grid.className = "grid";

    concepts.forEach((c, index) => {
      const p = progress[c.mmuId] ?? {};
      const mustPlay = p.mustPlay ?? 1;
      const playSoFar = p.playSoFar ?? 0;
      const completed = p.completed ?? playSoFar >= mustPlay;

      const tile = document.createElement("button");
      tile.type = "button";
      tile.className = "tile";
      tile.dataset.mmuId = String(c.mmuId);
      tile.dataset.index = String(index);
      if (c.mmuId === current) tile.classList.add("current");

      const url = this._urls?.[c.mmuId];
      if (needsImage(c)) {
        tile.classList.add("image");
        if (url) {
          const img = document.createElement("img");
          img.src = url;
          img.alt = c.phrase;
          img.loading = "lazy";
          tile.appendChild(img);
        } else {
          tile.classList.add("no-image");
          tile.appendChild(document.createTextNode(c.phrase));
        }
      } else {
        // Text / AudioOnly tiles show the word (KFESRListPageVC.m:89). iOS
        // falls back to "(Audio)" only when textEn is empty; our MMU phrase
        // is always populated, so the word renders.
        tile.appendChild(document.createTextNode(c.phrase || "(Audio)"));
      }

      // Counter circle: green tick when complete, else "playSoFar/mustPlay".
      const badge = document.createElement("span");
      badge.className = "badge";
      if (completed) {
        badge.classList.add("complete");
        badge.textContent = "✓";
        badge.setAttribute("aria-label", "completed");
      } else {
        badge.textContent = `${playSoFar}/${mustPlay}`;
      }
      tile.appendChild(badge);

      tile.addEventListener("click", () =>
        this.emit("tile:tap", { mmuId: c.mmuId, index }),
      );
      grid.appendChild(tile);
    });

    return html`${grid}`;
  }
}

/** @param {Concept} c */
function needsImage(c) {
  return c.displayType === "audio_image" || c.displayType === "audio_image_text";
}
